import React, { useState } from "react";
import {
    ActivityTimeline,
    ActivityTimelineLog,
} from "@design-system/ActivityTimeline/ActivityTimeline";
import { BooleanControl, PlaygroundSelect } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DataDisplayPlayground.module.scss";

type EventCount = "empty" | "few" | "many";

const eventCountOptions = [
    "empty",
    "few",
    "many",
] satisfies readonly EventCount[];

const now = Date.now();

const activityEvents = [
    {
        id: "categorized",
        actor: "Daniel Kim",
        action: "categorized the transaction as Software & subscriptions.",
        details: "Account changed from Uncategorized expense to 6260 Software.",
        timestamp: new Date(now - 12 * 60 * 1000),
    },
    {
        id: "memo",
        actor: "Kick AI",
        action: "added a memo from the attached receipt.",
        details:
            "Annual workspace plan covering August 2026 through July 2027.",
        timestamp: new Date(now - 48 * 60 * 1000),
    },
    {
        id: "matched",
        actor: "Maya Chen",
        action: "matched the $2,480.00 payment to invoice INV-2026-0842.",
        details: "The invoice is now fully paid.",
        timestamp: new Date(now - 3 * 60 * 60 * 1000),
    },
    {
        id: "imported",
        actor: "Mercury",
        action: "imported the transaction into Operating checking ••4821.",
        details:
            "Posted August 12, 2026 with bank description ACME CLOUD SVCS.",
        timestamp: new Date(now - 26 * 60 * 60 * 1000),
    },
];

const getEvents = (eventCount: EventCount) => {
    if (eventCount === "empty") {
        return [];
    }
    if (eventCount === "few") {
        return activityEvents.slice(0, 2);
    }
    return activityEvents;
};

export const ActivityTimelinePlayground = () => {
    const [eventCount, setEventCount] = useState<EventCount>("many");
    const [hasDetails, setHasDetails] = useState(true);

    const events = getEvents(eventCount);

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Events</h2>
                        <PlaygroundSelect<EventCount>
                            options={eventCountOptions}
                            value={eventCount}
                            onChange={setEventCount}
                        />
                    </section>
                    <section>
                        <h2>Content</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Show details"
                                checked={hasDetails}
                                onChange={setHasDetails}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${events.length} events`}
                output={JSON.stringify(
                    {
                        props: {
                            children: `${events.length} activity logs`,
                        },
                        showcase: {
                            eventCount,
                            hasDetails,
                        },
                    },
                    null,
                    2,
                )}
            >
                <div className={styles.surface}>
                    <ActivityTimeline>
                        {events.map((event) => (
                            <ActivityTimelineLog
                                key={event.id}
                                timestamp={event.timestamp}
                                details={
                                    hasDetails ? (
                                        <span
                                            className={styles.timelineDetails}
                                        >
                                            {event.details}
                                        </span>
                                    ) : undefined
                                }
                            >
                                <strong>{event.actor}</strong> {event.action}
                            </ActivityTimelineLog>
                        ))}
                    </ActivityTimeline>
                    {events.length === 0 && (
                        <div className={styles.emptyState}>No activity yet</div>
                    )}
                </div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
